/**
 * Query Key Factory
 *
 * Centralized query key management for React Query.
 * Provides consistent, hierarchical keys with type safety.
 *
 * Benefits:
 * - Prevents typos and inconsistencies
 * - Easy to invalidate related queries
 * - Self-documenting query structure
 * - Type-safe query key generation
 *
 * @example
 * ```tsx
 * // Use in a query
 * useQuery({
 *   queryKey: queryKeys.tasks.list(orgId, { status: 'active' }),
 *   queryFn: () => fetchTasks(orgId, { status: 'active' })
 * });
 *
 * // Invalidate all task queries
 * queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
 *
 * // Invalidate specific task
 * queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(taskId) });
 * ```
 */

export const queryKeys = {
  // All queries
  all: ['forge'] as const,

  // Tasks
  tasks: {
    all: ['forge', 'tasks'] as const,
    lists: () => [...queryKeys.tasks.all, 'list'] as const,
    list: (orgId: string, filters?: Record<string, any>) =>
      [...queryKeys.tasks.lists(), orgId, filters] as const,
    details: () => [...queryKeys.tasks.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.tasks.details(), id] as const,
  },

  // Requests
  requests: {
    all: ['forge', 'requests'] as const,
    lists: () => [...queryKeys.requests.all, 'list'] as const,
    list: (orgId: string, filters?: Record<string, any>) =>
      [...queryKeys.requests.lists(), orgId, filters] as const,
    details: () => [...queryKeys.requests.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.requests.details(), id] as const,
  },

  // Finance Queue
  financeQueue: {
    all: ['forge', 'finance-queue'] as const,
    lists: () => [...queryKeys.financeQueue.all, 'list'] as const,
    list: (orgId: string, filters?: Record<string, any>) =>
      [...queryKeys.financeQueue.lists(), orgId, filters] as const,
    page: (orgId: string, statusFilter?: string, page = 1, pageSize = 50) =>
      [...queryKeys.financeQueue.all, 'page', orgId, statusFilter ?? 'all', page, pageSize] as const,
    details: () => [...queryKeys.financeQueue.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.financeQueue.details(), id] as const,
    stats: (orgId: string) => [...queryKeys.financeQueue.all, 'stats', orgId] as const,
    statusCounts: (orgId: string) => [...queryKeys.financeQueue.all, 'status-counts', orgId] as const,
  },

  // Work Items (unified view)
  workItems: {
    all: ['forge', 'work-items'] as const,
    lists: () => [...queryKeys.workItems.all, 'list'] as const,
    list: (orgId: string, filters?: Record<string, any>) =>
      [...queryKeys.workItems.lists(), orgId, filters] as const,
    userWorkload: (orgId: string, userId: string) =>
      [...queryKeys.workItems.all, 'workload', orgId, userId] as const,
  },

  // People & Directory
  people: {
    all: ['forge', 'people'] as const,
    lists: () => [...queryKeys.people.all, 'list'] as const,
    list: (orgId: string, filters?: Record<string, any>) =>
      [...queryKeys.people.lists(), orgId, filters] as const,
    details: () => [...queryKeys.people.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.people.details(), id] as const,
  },

  // Contributions
  contributions: {
    all: ['forge', 'contributions'] as const,
    lists: () => [...queryKeys.contributions.all, 'list'] as const,
    list: (orgId: string, filters?: Record<string, any>) =>
      [...queryKeys.contributions.lists(), orgId, filters] as const,
    page: (orgId: string, dateRange?: string, page = 1, pageSize = 50, filters?: Record<string, any>) =>
      [...queryKeys.contributions.all, 'page', orgId, dateRange ?? 'all', page, pageSize, filters] as const,
    details: () => [...queryKeys.contributions.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.contributions.details(), id] as const,
    byPerson: (orgId: string, personId: string) =>
      [...queryKeys.contributions.all, 'person', orgId, personId] as const,
  },

  // Events & Attendance
  events: {
    all: ['forge', 'events'] as const,
    lists: () => [...queryKeys.events.all, 'list'] as const,
    list: (orgId: string, filters?: Record<string, any>) =>
      [...queryKeys.events.lists(), orgId, filters] as const,
    details: () => [...queryKeys.events.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.events.details(), id] as const,
  },

  attendance: {
    all: ['forge', 'attendance'] as const,
    // No trailing `undefined` without filters: TanStack matches a key prefix element by element,
    // so [..., orgId, undefined] never matched a filtered dashboard query and its invalidations
    // (after a check-in, a roster save or a pickup) refreshed nothing.
    dashboard: (orgId: string, filters?: Record<string, any>) =>
      filters === undefined
        ? ([...queryKeys.attendance.all, 'dashboard', orgId] as const)
        : ([...queryKeys.attendance.all, 'dashboard', orgId, filters] as const),
    roster: (orgId: string, eventId: string) => [...queryKeys.attendance.all, 'roster', orgId, eventId] as const,
    byEvent: (eventId: string) => [...queryKeys.attendance.all, 'event', eventId] as const,
    byPerson: (personId: string) => [...queryKeys.attendance.all, 'person', personId] as const,
  },

  // Comments & Activity
  comments: {
    all: ['forge', 'comments'] as const,
    byRecord: (recordType: string, recordId: string) =>
      [...queryKeys.comments.all, recordType, recordId] as const,
  },

  // Linked Records
  linkedRecords: {
    all: ['forge', 'linked-records'] as const,
    bySource: (sourceType: string, sourceId: string) =>
      [...queryKeys.linkedRecords.all, 'source', sourceType, sourceId] as const,
    byTarget: (targetType: string, targetId: string) =>
      [...queryKeys.linkedRecords.all, 'target', targetType, targetId] as const,
  },

  // Attachments
  attachments: {
    all: ['forge', 'attachments'] as const,
    byEntity: (entityType: string, entityId: string) =>
      [...queryKeys.attachments.all, entityType, entityId] as const,
  },

  // Projects
  projects: {
    all: ['forge', 'projects'] as const,
    lists: () => [...queryKeys.projects.all, 'list'] as const,
    list: (orgId: string, filters?: Record<string, any>) =>
      [...queryKeys.projects.lists(), orgId, filters] as const,
    details: () => [...queryKeys.projects.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.projects.details(), id] as const,
  },

  // Funds & Allocations
  funds: {
    all: ['forge', 'funds'] as const,
    lists: () => [...queryKeys.funds.all, 'list'] as const,
    list: (orgId: string) => [...queryKeys.funds.lists(), orgId] as const,
    details: () => [...queryKeys.funds.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.funds.details(), id] as const,
  },

  allocations: {
    all: ['forge', 'allocations'] as const,
    byFund: (fundId: string) => [...queryKeys.allocations.all, 'fund', fundId] as const,
    byProject: (projectId: string) => [...queryKeys.allocations.all, 'project', projectId] as const,
  },

  // User Preferences & Settings
  userPreferences: {
    all: ['forge', 'user-preferences'] as const,
    detail: (userId: string) => [...queryKeys.userPreferences.all, userId] as const,
  },

  // Notifications
  notifications: {
    all: ['forge', 'notifications'] as const,
    list: (userId: string) => [...queryKeys.notifications.all, 'list', userId] as const,
    unreadCount: (userId: string) => [...queryKeys.notifications.all, 'unread', userId] as const,
  },

  // Dashboard & Analytics
  dashboard: {
    all: ['forge', 'dashboard'] as const,
    financeDashboard: (orgId: string, filters?: Record<string, any>) =>
      [...queryKeys.dashboard.all, 'finance', orgId, filters] as const,
    workloadDashboard: (orgId: string, userId?: string) =>
      [...queryKeys.dashboard.all, 'workload', orgId, userId] as const,
  },

  // Volunteers
  volunteers: {
    all: ['forge', 'volunteers'] as const,
    availability: (personId: string) =>
      [...queryKeys.volunteers.all, 'availability', personId] as const,
    preferences: (personId: string) =>
      [...queryKeys.volunteers.all, 'preferences', personId] as const,
  },

  // HR Module
  hr: {
    all: ['forge', 'hr'] as const,
    staff: (orgId: string) => [...queryKeys.hr.all, 'staff', orgId] as const,
    positions: (orgId: string) => [...queryKeys.hr.all, 'positions', orgId] as const,
    timeOff: (orgId: string) => [...queryKeys.hr.all, 'time-off', orgId] as const,
  },

  // Feature Flags
  featureFlags: {
    all: ['forge', 'feature-flags'] as const,
    byOrg: (orgId: string) => [...queryKeys.featureFlags.all, orgId] as const,
  },

  // Search
  search: {
    all: ['forge', 'search'] as const,
    query: (query: string, filters?: Record<string, any>) =>
      [...queryKeys.search.all, query, filters] as const,
  },
} as const;

/**
 * Helper to invalidate all queries for a specific organization
 */
export const getOrgQueryPattern = (orgId: string) => {
  return { predicate: (query: any) => {
    const key = query.queryKey;
    return Array.isArray(key) && key.includes(orgId);
  }};
};

/**
 * Helper to invalidate queries by record type
 */
export const getRecordQueryPattern = (recordType: string, recordId: string) => {
  return { predicate: (query: any) => {
    const key = query.queryKey;
    return Array.isArray(key) && key.includes(recordType) && key.includes(recordId);
  }};
};
